/**
 * Draw the chart the analytics agent asked for.
 *
 * The agent returns a complete specification beside the rows:
 *
 *   { type, title, x, y, group_by, sort, y_format, reason }
 *
 * x and y name column aliases in that same result. y is one column, or a list
 * of up to four sharing the x axis — "partner wise net add and business
 * generated" is two. Before the list existed the model put the first metric in
 * y and described the second in `reason`, a free-text field nothing reads, so
 * the second metric never reached anyone.
 *
 * The prompt requires those aliases to exist in the SELECT; this component
 * checks rather than trusting it, and draws nothing when the check fails. A
 * chart of the wrong columns is worse than no chart — the table below is still
 * correct, and silence sends the reader there.
 *
 * Every guard returns null so the table stands alone. Nothing here throws.
 */

import React from "react";
import {
  ResponsiveContainer,
  BarChart, Bar,
  LineChart, Line,
  AreaChart, Area,
  PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

/** Types this component draws. Anything else falls back to the table.
 *
 *  stacked_bar, scatter and heatmap have no agreed shape yet — no real payload
 *  has carried one. Guessing at them from the prompt text is how a chart ends
 *  up confidently wrong. */
const DRAWN = new Set(["bar", "horizontal_bar", "grouped_bar", "line", "area", "pie"]);

/** At most this many series on one chart. Ten categories times five series is
 *  a picket fence, not a comparison. */
const MAX_SERIES = 4;

/** Two metrics only belong on one axis when they are the same order of
 *  magnitude. Revenue in crores beside a count of roles draws the count as a
 *  flat line at zero — which reads as missing data rather than as a scale
 *  problem, and is the more dangerous of the two. */
const MAX_SPREAD = 100;

/** Above this, a bar chart is a picket fence and a pie is unreadable.
 *  Line and area are exempt: many points is what they are for. */
const CATEGORY_LIMIT = 25;

/** Enough of a palette for a grouped legend or a pie, ordered so neighbouring
 *  slices stay distinguishable. Leads with the widget's own accent. */
const SERIES = [
  "#d4691f", "#4c9aff", "#3ec9a7", "#d8b83f",
  "#a98bdb", "#e2735f", "#5fb0e2", "#8fc866",
];

const GRID = "#262e39";
const AXIS = "#9aa2ae";

/** MySQL hands DECIMAL back as a string — "932715099.56861000". Recharts needs
 *  a number. A value that will not parse is dropped from the chart rather than
 *  plotted as zero, which would read as a real measurement of nothing. */
function toNumber(value) {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

/** Axis and tooltip formatter for the y values.
 *
 *  currency_inr uses en-IN, which groups as 33,58,08,625 rather than
 *  335,808,625. That is what PINE shows everywhere else, and the grouping is
 *  the whole point of the hint. */
function makeFormatter(yFormat) {
  const inr = (n) => "₹" + n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const usd = (n) => "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });

  switch (yFormat) {
    case "currency_inr": return inr;
    case "currency_usd": return usd;
    case "percent":      return (n) => n.toLocaleString("en-IN", { maximumFractionDigits: 1 }) + "%";
    case "number":       return (n) => n.toLocaleString("en-IN", { maximumFractionDigits: 0 });
    default:             return (n) => String(n);
  }
}

/** Shorten an axis tick so the labels do not collide. 33,58,08,625 becomes
 *  33.6 Cr. The tooltip still carries the exact figure. */
function makeTickFormatter(yFormat) {
  const money = yFormat === "currency_inr" || yFormat === "currency_usd";
  const symbol = yFormat === "currency_usd" ? "$" : "₹";

  return (n) => {
    if (typeof n !== "number" || !Number.isFinite(n)) return "";
    const abs = Math.abs(n);
    const head = money ? symbol : "";
    if (yFormat === "percent") return n.toFixed(0) + "%";
    if (abs >= 1e7) return head + (n / 1e7).toFixed(1) + " Cr";
    if (abs >= 1e5) return head + (n / 1e5).toFixed(1) + " L";
    if (abs >= 1e3) return head + (n / 1e3).toFixed(0) + "K";
    return head + String(n);
  };
}

/** Apply the agent's sort rather than trusting the order the rows arrived in.
 *
 *  They often agree — a y_desc ranking usually comes back sorted, which makes
 *  it easy to assume row order can be relied on. It cannot: x_asc on a time
 *  series would otherwise plot in whatever order the SQL happened to return. */
function applySort(rows, sort, xKey, yKey) {
  if (!sort) return rows;

  const copy = rows.slice();
  const byX = (a, b) => String(a[xKey]).localeCompare(String(b[xKey]), undefined, { numeric: true });
  const byY = (a, b) => (a[yKey] ?? 0) - (b[yKey] ?? 0);

  switch (sort) {
    case "x_asc":  return copy.sort(byX);
    case "x_desc": return copy.sort((a, b) => byX(b, a));
    case "y_asc":  return copy.sort(byY);
    case "y_desc": return copy.sort((a, b) => byY(b, a));
    default:       return rows;
  }
}
const CHART_BG = "#12161d";
const LABEL = "#9aa2ae";
const SCALE = 2;
const FONT = "ui-sans-serif, system-ui, -apple-system, 'Segoe UI', sans-serif";

/** Serialize the plot.
 *
 *  Axis and label colours come from the stylesheet, which does not travel with
 *  the markup, so the font rule is inlined. Colours are already presentation
 *  attributes on the elements themselves and survive as they are. */
function svgMarkup(svg, width, height) {
  const clone = svg.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", String(width));
  clone.setAttribute("height", String(height));

  const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
  style.textContent = `text{font-family:${FONT}}`;
  clone.insertBefore(style, clone.firstChild);

  return new XMLSerializer().serializeToString(clone);
}

function loadImage(markup) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("svg did not load"));
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(markup);
  });
}

const SWATCH = 8;
const SWATCH_GAP = 6;
const ITEM_GAP = 14;
const EDGE = 10;
const ROW_HEIGHT = 16;

/** Recharts renders the legend as HTML beside the SVG, so it is absent from the
 *  serialized markup. Laid out here from the same keys and colours the chart
 *  drew with, wrapping onto a second row when it runs out of width. */
function legendRows(ctx, legend, width) {
  ctx.font = `10px ${FONT}`;

  const rows = [];
  let row = [];
  let x = EDGE;

  for (const item of legend) {
    const w = SWATCH + SWATCH_GAP + ctx.measureText(item.label).width + ITEM_GAP;
    if (x + w > width - EDGE && row.length) {
      rows.push(row);
      row = [];
      x = EDGE;
    }
    row.push({ ...item, x });
    x += w;
  }
  if (row.length) rows.push(row);

  return rows;
}

function drawLegend(ctx, rows, top) {
  ctx.font = `10px ${FONT}`;
  ctx.textBaseline = "middle";

  rows.forEach((row, i) => {
    const y = top + i * ROW_HEIGHT + ROW_HEIGHT / 2;
    for (const item of row) {
      ctx.fillStyle = item.color;
      ctx.fillRect(item.x, y - SWATCH / 2, SWATCH, SWATCH);
      ctx.fillStyle = LABEL;
      ctx.fillText(item.label, item.x + SWATCH + SWATCH_GAP, y);
    }
  });
}

function canvasToBlob(canvas) {
  return new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
}

/** Copy the chart as a PNG.
 *
 *  Firefox cannot write an image to the clipboard, and clipboard access needs a
 *  secure context. Both land in the same fallback: hand over the file. */
async function copyChart(host, title, legend) {
  const svg = host && host.querySelector("svg");
  if (!svg) return "failed";

  const box = svg.getBoundingClientRect();
  const width = Math.max(1, Math.round(box.width));
  const plot = Math.max(1, Math.round(box.height));

  let img;
  try {
    img = await loadImage(svgMarkup(svg, width, plot));
  } catch {
    return "failed";
  }

  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  const rows = legend && legend.length ? legendRows(ctx, legend, width) : [];
  const head = title ? 22 : 8;
  const foot = rows.length ? rows.length * ROW_HEIGHT + 8 : 8;
  const height = head + plot + foot;

  canvas.width = width * SCALE;
  canvas.height = height * SCALE;
  ctx.scale(SCALE, SCALE);

  ctx.fillStyle = CHART_BG;
  ctx.fillRect(0, 0, width, height);

  if (title) {
    ctx.fillStyle = LABEL;
    ctx.font = `600 11px ${FONT}`;
    ctx.textBaseline = "middle";
    ctx.fillText(title, EDGE, head / 2);
  }

  ctx.drawImage(img, 0, head, width, plot);
  if (rows.length) drawLegend(ctx, rows, head + plot);

  const blob = await canvasToBlob(canvas);
  if (!blob) return "failed";

  try {
    await navigator.clipboard.write([
      new window.ClipboardItem({ "image/png": blob }),
    ]);
    return "copied";
  } catch {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${(title || "chart")
      .replace(/[^a-z0-9]+/gi, "_")
      .toLowerCase()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(url), 4000);
    return "saved";
  }
}

/** The card around a drawn chart.
 *
 *  Separate component because ResultChart returns null from a dozen guards
 *  before it draws anything, and a hook cannot sit behind those returns. */
function ChartFrame({ title, legend, children }) {
  const host = React.useRef(null);
  const [state, setState] = React.useState("");

  const copy = async () => {
    setState("...");
    setState(await copyChart(host.current, title, legend));
    setTimeout(() => setState(""), 1600);
  };

  return (
    <div className="ap-chart" ref={host}>
      <div className="ap-chart-head">
        {title ? <span className="ap-chart-title">{title}</span> : null}
        <button type="button" className="ap-copy" onClick={copy}>
          {state || "copy"}
        </button>
      </div>
      {children}
    </div>
  );
}

export function ResultChart({ data, graph }) {
  if (!graph || !data?.columns?.length || !data?.rows?.length) return null;

  const { type, title, x: xKey, y, sort, y_format: yFormat } = graph;

  if (!DRAWN.has(type)) return null;
  if (!xKey || !y) return null;

  // y is one column or several sharing the x axis. "partner wise net add and
  // business generated" is two.
  const yKeys = (Array.isArray(y) ? y : [y]).filter(Boolean);
  if (!yKeys.length || yKeys.length > MAX_SERIES) return null;

  // The prompt requires x and y to name selected columns. Verified here because
  // a prompt is an instruction, not a guarantee.
  if (!data.columns.includes(xKey)) return null;
  if (!yKeys.every((k) => data.columns.includes(k))) return null;

  // A row is kept only when every series on it parses. Charting a row with one
  // bar missing would show a partner as having no business generated when the
  // truth is that the value could not be read.
  const points = data.rows
    .map((row) => {
      const point = { [xKey]: row[xKey] };
      for (const key of yKeys) {
        const value = toNumber(row[key]);
        if (value === null) return null;
        point[key] = value;
      }
      return point;
    })
    .filter(Boolean);

  // One point is a number, not a chart. The sentence above already said it.
  if (points.length < 2) return null;

  // The prompt states the comparability rule; this enforces it. Same reason as
  // the column check above.
  if (yKeys.length > 1) {
    const peaks = yKeys
      .map((key) => Math.max(...points.map((p) => Math.abs(p[key]))))
      .filter((peak) => peak > 0);
    if (peaks.length && Math.max(...peaks) / Math.min(...peaks) > MAX_SPREAD) {
      return null;
    }
  }

  // A pie shows one quantity as parts of a whole; several series have no such
  // whole. Fall back rather than draw the first and drop the rest silently.
  if (type === "pie" && yKeys.length > 1) return null;

  const categorical =
    type === "bar" || type === "horizontal_bar" || type === "grouped_bar" || type === "pie";
  if (categorical && points.length > CATEGORY_LIMIT) return null;

  const sorted = applySort(points, sort, xKey, yKeys[0]);
  const format = makeFormatter(yFormat);
  const tick = makeTickFormatter(yFormat);

  const multi = yKeys.length > 1;

  const tooltip = (
    <Tooltip
      // recharts passes the series name as the second argument, which is the
      // column alias. With several series that is the only thing telling net
      // add from business generated.
      formatter={(value, name) => [format(value), name]}
      contentStyle={{
        background: "#1c222b",
        border: "1px solid " + GRID,
        borderRadius: 6,
        fontSize: 11,
        color: "#dfe4ea",
      }}
      cursor={{ fill: "rgba(255,255,255,0.04)" }}
    />
  );

  const axisProps = { stroke: AXIS, tick: { fill: AXIS, fontSize: 10 } };

  const legend = multi ? <Legend wrapperStyle={{ fontSize: 10, color: AXIS }} /> : null;
  // The same entries recharts is about to render as HTML, kept as data so the
  // copied image can carry them.
  const legendItems = multi
    ? yKeys.map((key, i) => ({ label: key, color: SERIES[i % SERIES.length] }))
    : type === "pie"
      ? sorted.map((p, i) => ({
          label: String(p[xKey]),
          color: SERIES[i % SERIES.length],
        }))
      : null;

  let chart;

  if (type === "pie") {
    chart = (
      <PieChart>
        <Pie data={sorted} dataKey={yKeys[0]} nameKey={xKey} outerRadius="75%">
          {sorted.map((_, i) => (
            <Cell key={i} fill={SERIES[i % SERIES.length]} />
          ))}
        </Pie>
        {tooltip}
        <Legend wrapperStyle={{ fontSize: 10, color: AXIS }} />
      </PieChart>
    );
  } else if (type === "horizontal_bar") {
    chart = (
      <BarChart data={sorted} layout="vertical" margin={{ left: 8, right: 12, top: 8, bottom: 4 }}>
        <CartesianGrid stroke={GRID} horizontal={false} />
        <XAxis type="number" tickFormatter={tick} {...axisProps} />
        <YAxis type="category" dataKey={xKey} width={110} {...axisProps} />
        {tooltip}
        {legend}
        {yKeys.map((key, i) => (
          <Bar key={key} dataKey={key} fill={SERIES[i % SERIES.length]} radius={[0, 3, 3, 0]} />
        ))}
      </BarChart>
    );
  } else if (type === "line" || type === "area") {
    const Chart = type === "line" ? LineChart : AreaChart;
    chart = (
      <Chart data={sorted} margin={{ left: 8, right: 12, top: 8, bottom: 4 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey={xKey} {...axisProps} />
        <YAxis tickFormatter={tick} width={58} {...axisProps} />
        {tooltip}
        {legend}
        {yKeys.map((key, i) =>
          type === "line" ? (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={SERIES[i % SERIES.length]}
              strokeWidth={2}
              dot={false}
            />
          ) : (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              stroke={SERIES[i % SERIES.length]}
              fill={SERIES[i % SERIES.length]}
              fillOpacity={0.22}
            />
          ),
        )}
      </Chart>
    );
  } else {
    // bar and grouped_bar are the same chart. With one series it is a bar;
    // with several, recharts places them side by side per category on its own.
    // The model emits grouped_bar for a single metric often enough that
    // treating them separately would only lose charts.
    chart = (
      <BarChart data={sorted} margin={{ left: 8, right: 12, top: 8, bottom: 4 }}>
        <CartesianGrid stroke={GRID} vertical={false} />
        <XAxis dataKey={xKey} interval={0} angle={-30} textAnchor="end" height={62} {...axisProps} />
        <YAxis tickFormatter={tick} width={58} {...axisProps} />
        {tooltip}
        {legend}
        {yKeys.map((key, i) => (
          <Bar key={key} dataKey={key} fill={SERIES[i % SERIES.length]} radius={[3, 3, 0, 0]} />
        ))}
      </BarChart>
    );
  }

  return (
    <ChartFrame title={title} legend={legendItems}>
      <ResponsiveContainer width="100%" height={type === "pie" ? 240 : 220}>
        {chart}
      </ResponsiveContainer>
    </ChartFrame>
  );
}

export default ResultChart;
